import { useEffect, useState, type FormEvent } from "react";
import { ArrowLeft, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import type { CognitoUser } from "amazon-cognito-identity-js";

import { authErrorKey, completeNewPassword, signIn, signUp } from "@/auth/cognito/client";
import { useSession } from "@/auth/session/SessionProvider";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PRIVACY_POLICY_URL } from "@/lib/legalLinks";

import logoHorizontal from "@/assets/brand/scheda-logo-horizontal.svg";

import { Field, FormError, GoogleButton, PasswordField } from "./fields";

/**
 * The sign-in / sign-up surface presented as a "metallic" card floating over the
 * Scheda landing, instead of a full-page navigation. Opened from the landing's
 * Log in / Sign up buttons; dismissed with Esc, the ← button, the backdrop, or
 * the browser Back button (the landing drives visibility from the {@code ?auth}
 * search param, so Back simply drops the param).
 *
 * <p>Its header carries the {@code LanguageSwitcher} as well as the close button. The
 * surrounding landing copy is not keyed for translation yet, so this card can render in
 * Danish over an English page (region detection, not the browser's language); the switcher
 * is what lets the visitor settle it without dismissing the form.</p>
 *
 * <p>It deliberately reuses the exact same primitives as the full {@link SignInPage}
 * / {@link SignUpPage} — the SRP {@code signIn}/{@code signUp} client, the shared
 * form {@link Field}s, and the i18n keys — so there is one auth implementation, not
 * two. The email-driven steps that can't live in a modal (confirm-your-email after
 * sign-up, and password reset) still navigate to their own pages.</p>
 */
export function AuthModal({
  mode,
  from = "/",
  onClose,
  onSwitch,
}: {
  /** Which form to show, or null when closed. */
  mode: "signin" | "signup" | null;
  /** Where to land after a successful sign-in (the deep link the visitor was headed to). */
  from?: string;
  onClose: () => void;
  /** Toggle between sign-in and sign-up without leaving the modal. */
  onSwitch: (mode: "signin" | "signup") => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useSession();

  const [email, setEmail] = useState("");
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<CognitoUser | null>(null);
  const [newPassword, setNewPassword] = useState("");

  // Esc to dismiss + lock the background from scrolling while open.
  useEffect(() => {
    if (!mode) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      // Escape belongs to the innermost thing that is open. The header's language dropdown
      // is a layer this card owns, and it closes itself on Escape; without this check the
      // card closed too, so backing out of the list threw away the whole form. Radix marks
      // its open popovers with this wrapper and only removes it on the re-render AFTER this
      // handler runs, so the node is still there to be seen.
      if (document.querySelector("[data-radix-popper-content-wrapper]")) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mode, onClose]);

  // Reset transient state whenever the form switches or reopens.
  useEffect(() => {
    setError(null);
    setChallenge(null);
    setPassword("");
    setNewPassword("");
  }, [mode]);

  if (!mode) return null;

  async function onSignIn(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await signIn(email, password);
      if (result.status === "NEW_PASSWORD_REQUIRED") {
        setChallenge(result.user);
        return;
      }
      session.adoptCognitoSession(result.session);
      navigate(from, { replace: true });
    } catch (err) {
      const code = (err as { code?: string }).code ?? "UnknownError";
      // Unconfirmed = an unfinished sign-up; send them to the email-code step.
      if (code === "UserNotConfirmedException") {
        navigate(`/signup/confirm?email=${encodeURIComponent(email.trim())}`);
        return;
      }
      setError(t(authErrorKey(code)));
    } finally {
      setBusy(false);
    }
  }

  async function onSetNewPassword(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      const cognitoSession = await completeNewPassword(challenge, newPassword);
      session.adoptCognitoSession(cognitoSession);
      navigate(from, { replace: true });
    } catch (err) {
      setError(t(authErrorKey((err as { code?: string }).code ?? "UnknownError")));
    } finally {
      setBusy(false);
    }
  }

  async function onSignUp(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signUp({ email, password, givenName, familyName });
      // The confirmation code arrives by email — that step is its own page.
      navigate(`/signup/confirm?email=${encodeURIComponent(email.trim())}`);
    } catch (err) {
      setError(t(authErrorKey((err as { code?: string }).code ?? "UnknownError")));
    } finally {
      setBusy(false);
    }
  }

  const title = challenge
    ? t("auth.newPassword.title")
    : mode === "signup"
      ? t("auth.signUp.title")
      : t("auth.signIn.title");
  const subtitle = challenge ? t("auth.newPassword.subtitle") : null;

  return (
    <div
      className="authback"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        // Close only on a genuine backdrop click: one whose target IS the backdrop, not
        // merely something the card does not contain in the DOM.
        //
        // The distinction is load-bearing now that the header holds a popover. React
        // propagates events along the REACT tree, not the DOM tree, so a mousedown inside
        // the language dropdown — portalled to document.body, and therefore outside
        // the card's own subtree — still bubbles to this handler as a descendant of the modal. The old
        // DOM-containment test read that as a backdrop click and dismissed the whole card,
        // so choosing a language silently cancelled signing in.
        //
        // Comparing against currentTarget keeps the original intent (the padding around the
        // card still dismisses, and a drag released off-card still does not) while any
        // layer the card owns, portalled or not, is left alone.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="authcard">
        <div className="authhead">
          <button type="button" className="authicon" aria-label={t("common.back", "Back")} onClick={onClose}>
            <ArrowLeft size={18} />
          </button>
          <img className="brandimg" src={logoHorizontal} alt="Scheda" style={{ height: "22px" }} />
          {/* The switcher belongs HERE and not only in the landing nav: this card is the one
              surface where the wrong language actually costs something, because it is a form.
              The nav is also behind the backdrop once the modal is open, so a visitor who
              only notices the mismatch at the form would have to dismiss it to fix it.
              z-[90] clears the .authback at z-80 — the dropdown is portalled to body. */}
          <div className="authend">
            <LanguageSwitcher className="langsw" contentClassName="z-[90]" />
            <button type="button" className="authicon" aria-label={t("common.close", "Close")} onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </div>

        <h2 className="authtitle">{title}</h2>
        {subtitle && <p className="authsub">{subtitle}</p>}

        {challenge ? (
          <form className="authform" onSubmit={onSetNewPassword}>
            <PasswordField
              label={t("auth.fields.newPassword")}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
            />
            <p className="authrules">{t("auth.passwordRules")}</p>
            <FormError>{error}</FormError>
            <button type="submit" className="btn cta authsubmit" disabled={busy}>
              {t("auth.newPassword.submit")}
            </button>
          </form>
        ) : mode === "signup" ? (
          <>
            <form className="authform" onSubmit={onSignUp}>
              <div className="authrow2">
                <Field
                  label={t("auth.fields.firstName")}
                  value={givenName}
                  onChange={(e) => setGivenName(e.target.value)}
                  autoComplete="given-name"
                  autoFocus
                  required
                />
                <Field
                  label={t("auth.fields.lastName")}
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  autoComplete="family-name"
                  required
                />
              </div>
              <Field
                label={t("auth.fields.email")}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
              <PasswordField
                label={t("auth.fields.password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
              />
              <p className="authrules">{t("auth.passwordRules")}</p>
              <FormError>{error}</FormError>
              <button type="submit" className="btn cta authsubmit" disabled={busy}>
                {t("auth.signUp.submit")}
              </button>
              <p className="authlegal">
                <Trans
                  i18nKey="auth.signUp.legal"
                  components={{
                    // No `terms` link: acceptance moved to the blocking TermsGate, where it is
                    // an explicit tick and is recorded. See SignUpPage for the same note.
                    privacy: <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" />,
                    cookies: <Link to="/cookies" />,
                  }}
                />
              </p>
            </form>
            <GoogleButton />
            <p className="authfoot">
              {t("auth.signUp.haveAccount")}{" "}
              <button type="button" className="alink" onClick={() => onSwitch("signin")}>
                {t("auth.signUp.signInInstead")}
              </button>
            </p>
          </>
        ) : (
          <>
            <form className="authform" onSubmit={onSignIn}>
              <Field
                label={t("auth.fields.email")}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                required
              />
              <PasswordField
                label={t("auth.fields.password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                hint={
                  <Link
                    to={`/forgot${email ? `?email=${encodeURIComponent(email.trim())}` : ""}`}
                    className="alink"
                  >
                    {t("auth.signIn.forgot")}
                  </Link>
                }
              />
              <FormError>{error}</FormError>
              <button type="submit" className="btn cta authsubmit" disabled={busy}>
                {t("auth.signIn.submit")}
              </button>
            </form>
            <GoogleButton />
            <p className="authfoot">
              {t("auth.signIn.noAccount")}{" "}
              <button type="button" className="alink" onClick={() => onSwitch("signup")}>
                {t("auth.signIn.createAccount")}
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
